import { useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Plain-MUI v5 replacement for the old MD2 `examples/Tables/DataTable`
 * (react-table based). Accepts the same `{ columns, rows }` shape so the
 * existing data hooks (layouts/tables/data/*) don't need to change —
 * only the rendering layer is new.
 *
 * columns: [{ Header, accessor, align, width, Cell, sortable }]
 * Cell receives ({ value, row: { original } }) — `original` is the row
 * object itself, matching the old react-table convention.
 */
function getValue(row, accessor) {
  if (typeof accessor === "function") return accessor(row);
  return row[accessor];
}

function DataTable({
  table,
  canSearch,
  searchLabel,
  entriesPerPage,
  showTotalEntries,
  isSorted,
  noEndBorder,
  serverPagination,
  loading,
}) {
  const { columns, rows } = table;
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");
  const [localPage, setLocalPage] = useState(0);
  const defaultRowsPerPage = entriesPerPage?.defaultValue || 10;
  const rowsPerPageOptions = entriesPerPage?.entries || [5, 10, 15, 20, 25];
  const [localRowsPerPage, setLocalRowsPerPage] = useState(defaultRowsPerPage);

  // In server mode `rows` is already the current page fetched from the API —
  // the parent owns page/rowsPerPage/count and refetches on change, so we
  // skip local filtering/slicing entirely (it would only ever see one page).
  const page = serverPagination ? serverPagination.page : localPage;
  const rowsPerPage = serverPagination ? serverPagination.rowsPerPage : localRowsPerPage;
  const setPage = serverPagination ? serverPagination.onPageChange : setLocalPage;
  const setRowsPerPage = serverPagination
    ? serverPagination.onRowsPerPageChange
    : setLocalRowsPerPage;

  const filteredRows = useMemo(() => {
    if (serverPagination || !search.trim()) return rows;
    const term = search.trim().toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => {
        const val = getValue(row, col.accessor);
        return val !== null && val !== undefined && String(val).toLowerCase().includes(term);
      })
    );
  }, [rows, search, columns, serverPagination]);

  const sortedRows = useMemo(() => {
    if (!isSorted || !orderBy) return filteredRows;
    const col = columns.find((c) => c.accessor === orderBy);
    if (!col) return filteredRows;
    const sorted = [...filteredRows].sort((a, b) => {
      const va = getValue(a, col.accessor);
      const vb = getValue(b, col.accessor);
      if (va === vb) return 0;
      if (va === null || va === undefined) return -1;
      if (vb === null || vb === undefined) return 1;
      return va > vb ? 1 : -1;
    });
    return order === "desc" ? sorted.reverse() : sorted;
  }, [filteredRows, isSorted, orderBy, order, columns]);

  const pagedRows = useMemo(() => {
    if (serverPagination) return sortedRows;
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage, serverPagination]);

  const rowCount = serverPagination ? serverPagination.count : sortedRows.length;

  const handleSort = (accessor, sortable) => {
    if (!isSorted || sortable === false) return;
    if (orderBy === accessor) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(accessor);
      setOrder("asc");
    }
  };

  const entriesStart = rowCount === 0 ? 0 : page * rowsPerPage + 1;
  const entriesEnd = Math.min(rowCount, (page + 1) * rowsPerPage);

  return (
    <TableContainer>
      {(entriesPerPage || canSearch) && (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          px={3}
          py={2}
          gap={2}
          flexWrap="wrap"
        >
          {canSearch && !serverPagination && (
            <TextField
              placeholder={searchLabel || "Search..."}
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ width: { xs: "100%", sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.accessor?.toString?.() || col.Header}
                align={col.align || "left"}
                sx={{ width: col.width || "auto" }}
              >
                {isSorted && col.sortable !== false ? (
                  <TableSortLabel
                    active={orderBy === col.accessor}
                    direction={orderBy === col.accessor ? order : "asc"}
                    onClick={() => handleSort(col.accessor, col.sortable)}
                  >
                    {col.Header}
                  </TableSortLabel>
                ) : (
                  col.Header
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading && pagedRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  No records found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            pagedRows.map((row, idx) => (
              <TableRow
                key={row.id ?? idx}
                sx={
                  noEndBorder && idx === pagedRows.length - 1
                    ? { "& .MuiTableCell-root": { borderBottom: "none" } }
                    : undefined
                }
              >
                {columns.map((col) => {
                  const value = getValue(row, col.accessor);
                  return (
                    <TableCell
                      key={col.accessor?.toString?.() || col.Header}
                      align={col.align || "left"}
                    >
                      {col.Cell ? <col.Cell value={value} row={{ original: row }} /> : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        px={3}
        py={2}
        gap={1}
      >
        {showTotalEntries && (
          <Typography variant="caption" color="text.secondary">
            Showing {entriesStart} to {entriesEnd} of {rowCount} entries
          </Typography>
        )}
        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newRowsPerPage = parseInt(e.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            // Server mode: the parent's onRowsPerPageChange is expected to
            // reset its own page state to 0 — calling setPage here too would
            // fire a second, redundant fetch.
            if (!serverPagination) setPage(0);
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          sx={{ border: "none" }}
        />
      </Box>
    </TableContainer>
  );
}

DataTable.defaultProps = {
  canSearch: false,
  searchLabel: "Search...",
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  showTotalEntries: true,
  isSorted: true,
  noEndBorder: false,
  serverPagination: null,
  loading: false,
};

DataTable.propTypes = {
  table: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
  }).isRequired,
  canSearch: PropTypes.bool,
  searchLabel: PropTypes.string,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  showTotalEntries: PropTypes.bool,
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  /**
   * Pass to switch the table into server-pagination mode: `table.rows` is
   * treated as just the current page (already fetched from the API), and
   * this object's page/rowsPerPage/count drive the pagination controls
   * instead of slicing locally.
   */
  serverPagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
  }),
  loading: PropTypes.bool,
};

export default DataTable;
